import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, CreditCard, TrendingUp, Activity, DollarSign,
  Server, HardDrive, Cpu, Database, Clock, AlertTriangle, CheckCircle,
  Search, Calendar, ArrowUp, ArrowDown, Crown, Layers,
  UserCheck, UserX, Zap, Star, BarChart3, Shield, PlayCircle, PauseCircle
} from 'lucide-react';
import api from '../../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const COLORS = ['#f78166', '#a371f7', '#3fb950', '#d29922', '#58a6ff', '#f85149', '#ff6f00', '#00bcd4'];

function SectionHeading({ icon, title }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 'var(--space-md)', marginTop: 'var(--space-sm)',
      paddingBottom: 'var(--space-sm)',
      borderBottom: '1px solid var(--border-primary)',
    }}>
      <span style={{ color: 'var(--accent-primary)' }}>{icon}</span>
      <h3 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h3>
    </div>
  );
}

export default function AdminDashboardNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  //country_stats = [];

  useEffect(() => { loadDashboard(); }, []);
  
  // Auto-refresh system health every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadDashboard, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/api/admin/dashboard');
      setData(res.data);
      try { const a = await api.get('/api/admin/recent-activity'); setActivity(a.data.events || []); } catch {}
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;
  if (!data) return <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Failed to load dashboard</p>;

  const health = data.system_health || {};
  const statusColors = {
    healthy: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', dot: '#10B981', text: '#10B981', label: 'All Systems Operational' },
    warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', dot: '#F59E0B', text: '#F59E0B', label: 'System Under Load' },
    degraded: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', dot: '#EF4444', text: '#EF4444', label: 'Attention Required' },
  };
  const statusStyle = statusColors[health.overall] || statusColors.healthy;

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      
      {/* System Status Banner */}
      <div style={{
        background: statusStyle.bg,
        border: `1px solid ${statusStyle.border}`,
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-md) var(--space-xl)',
        marginBottom: 'var(--space-xl)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: statusStyle.dot,
            boxShadow: `0 0 12px ${statusStyle.dot}`,
            animation: health.overall === 'degraded' ? 'pulse 1.5s ease-in-out infinite' : 'none',
          }} />
          <span style={{ color: statusStyle.text, fontWeight: 700, fontSize: 14 }}>
            {statusStyle.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)' }}>
          <span><Cpu size={12} style={{ marginRight: 4 }} />CPU {health.cpu_percent}%</span>
          <span><HardDrive size={12} style={{ marginRight: 4 }} />RAM {health.memory_used_gb}/{health.memory_total_gb}GB</span>
          <span><HardDrive size={12} style={{ marginRight: 4 }} />Disk {health.disk_used_gb}/{health.disk_total_gb}GB ({health.disk_percent}%)</span>
          <span><Server size={12} style={{ marginRight: 4 }} />Browsers {health.browser_active}/{health.browser_max}</span>
          <span><Database size={12} style={{ marginRight: 4 }} />WAL {health.wal_size_mb}MB</span>
          {health.stuck_jobs > 0 && (
            <span style={{ color: 'var(--error)' }}><AlertTriangle size={12} style={{ marginRight: 4 }} />{health.stuck_jobs} stuck jobs</span>
          )}
          <span><Activity size={12} style={{ marginRight: 4 }} />{health.running_jobs} active · {health.queued_jobs} queued</span>
          <span><Zap size={12} style={{ marginRight: 4 }} />{health.worker_count || 0} workers</span>
          <span style={{ color: 'var(--success)' }}><CheckCircle size={12} style={{ marginRight: 4 }} />{health.completed_jobs || 0} completed</span>
        </div>
      </div>

      {/* Section: Real-Time Operations */}
      <SectionHeading icon={<Activity size={18} />} title="Real-Time Operations" />
      {/* Real-Time Operations Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <OpsCard icon={<Search size={18} />} label="Active Searches" value={health.running_jobs || 0} color="var(--accent-primary)" sub={`${health.queued_jobs || 0} queued`} />
        <OpsCard icon={<AlertTriangle size={18} />} label="Stuck Jobs" value={health.stuck_jobs || 0} color={health.stuck_jobs > 0 ? 'var(--error)' : 'var(--success)'} sub={health.stuck_jobs > 0 ? 'Needs attention' : 'All clear'} />
        <OpsCard icon={<Layers size={18} />} label="Browser Pool" value={`${health.browser_active || 0}/${health.browser_max || 4}`} color={health.browser_active > 0 ? 'var(--success)' : 'var(--text-tertiary)'} sub={`${health.browser_available || 0} available`} />
        <OpsCard icon={<Database size={18} />} label="WAL Size" value={`${health.wal_size_mb || 0} MB`} color={health.wal_size_mb > 100 ? 'var(--warning)' : 'var(--success)'} sub="Write-ahead log" />
        <OpsCard icon={<Activity size={18} />} label="Total Jobs" value={health.total_jobs || 0} color="var(--info)" sub="All-time" />
      </div>

      {/* Section: Financial Overview */}
      <SectionHeading icon={<DollarSign size={18} />} title="Financial Overview" />
      {/* KPI Cards Row 1,  Financials */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<DollarSign size={22} />} label="MRR" value={`$${(data.financials?.mrr || 0).toLocaleString()}`} color="var(--success)" sub={`${data.financials?.mrr_growth || 0}% vs last month`} />
        <KPICard icon={<CreditCard size={22} />} label="ARR" value={`$${(data.financials?.arr || 0).toLocaleString()}`} color="var(--accent-secondary)" />
        <KPICard icon={<TrendingUp size={22} />} label="Revenue (MTD)" value={`$${(data.financials?.revenue_mtd || 0).toLocaleString()}`} color="var(--accent-primary)" />
        <KPICard icon={<Users size={22} />} label="Paying Users" value={data.financials?.paying_users || 0} color="var(--info)" sub={`${data.financials?.conversion_rate || 0}% conversion`} />
        <KPICard icon={<Zap size={22} />} label="Avg. Revenue/User" value={`$${data.financials?.arpu || 0}`} color="var(--warning)" />
      </div>

      {/* Section: User Metrics */}
      <SectionHeading icon={<Users size={18} />} title="User Metrics" />
      {/* KPI Cards Row 2,  Users */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<Users size={22} />} label="Total Users" value={data.users?.total || 0} color="var(--info)" sub={`${data.users?.new_today || 0} today`} />
        <KPICard icon={<UserCheck size={22} />} label="Verified" value={data.users?.verified || 0} color="var(--success)" />
        <KPICard icon={<UserX size={22} />} label="Unverified" value={data.users?.unverified || 0} color="var(--error)" />
        <KPICard icon={<Crown size={22} />} label="Free Users" value={data.users?.free || 0} color="var(--text-secondary)" />
        <KPICard icon={<Star size={22} />} label="Growth" value={data.users?.growth || 0} color="var(--accent-primary)" />
        <KPICard icon={<Zap size={22} />} label="Pro" value={data.users?.pro || 0} color="var(--accent-secondary)" />
      </div>

      {/* User Conversion Funnel */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)',
        marginBottom: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)',
      }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserCheck size={16} /> User Conversion Funnel
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { label: 'Total', value: data.users?.total || 0, color: 'var(--info)', pct: 100 },
            { label: 'Verified', value: data.users?.verified || 0, color: 'var(--success)', pct: Math.round(((data.users?.verified || 0) / Math.max(data.users?.total || 1, 1)) * 100) },
            { label: 'Onboarded', value: data.users?.onboarded || 0, color: 'var(--accent-primary)', pct: Math.round(((data.users?.onboarded || 0) / Math.max(data.users?.total || 1, 1)) * 100) },
            { label: 'Active (7d)', value: data.users?.active_7d || 0, color: 'var(--accent-secondary)', pct: Math.round(((data.users?.active_7d || 0) / Math.max(data.users?.total || 1, 1)) * 100) },
            { label: 'Active (30d)', value: data.users?.active_30d || 0, color: 'var(--warning)', pct: Math.round(((data.users?.active_30d || 0) / Math.max(data.users?.total || 1, 1)) * 100) },
          ].map((stage, i) => (
            <React.Fragment key={stage.label}>
              <div style={{ textAlign: 'center', minWidth: 120, padding: 'var(--space-md)' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: stage.color, lineHeight: 1 }}>{stage.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 500 }}>{stage.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{stage.pct}%</div>
              </div>
              {i < 4 && (
                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-tertiary)', fontSize: 18, padding: '0 8px' }}>
                  →
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Section: Analytics */}
      <SectionHeading icon={<BarChart3 size={18} />} title="Analytics & Trends" />
      {/* Charts Row 1,  Revenue Trend (full width) */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <ChartCard title="Revenue Trend (6 Months)" icon={<DollarSign size={16} />}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.revenue_trend || []}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3fb950" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3fb950" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
              <XAxis dataKey="month" stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Area type="monotone" dataKey="revenue" stroke="#3fb950" fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      

      {/* Charts Row 3,  Plan Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-xl)', marginBottom: 'var(--space-xl)' }}>
        <ChartCard title="Plan Distribution" icon={<Crown size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.plan_distribution || []} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                {data.plan_distribution?.map((_, i) => (
                  <Cell key={i} fill={['var(--text-secondary)', 'var(--accent-primary)', 'var(--accent-secondary)', '#f59e0b'][i % 4]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="User Growth (Weekly)" icon={<Users size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.user_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
              <XAxis dataKey="week" stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Bar dataKey="new_users" fill="#58a6ff" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Section: Activity */}
      <SectionHeading icon={<Clock size={18} />} title="Recent Activity" />
      {/* Recent Activity Feed */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)',
        marginBottom: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)',
      }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} /> Recent Activity
        </h3>
        {activity.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, textAlign: 'center', padding: 20 }}>No recent activity</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {activity.map((event, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 0',
                borderBottom: i < activity.length - 1 ? '1px solid var(--border-primary)' : 'none',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{event.icon}</span>
                <span style={{ flex: 1, color: 'var(--text-primary)', fontSize: 13 }}>{event.detail}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11, flexShrink: 0 }}>{event.time}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section: Quick Links */}
      <SectionHeading icon={<Zap size={18} />} title="Quick Links" />
      {/* Quick Links */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
        {[
          { label: 'Manage Users', path: '/dashboard/admin/users', icon: <Users size={16} /> },
          { label: 'Billing Controls', path: '/dashboard/admin/billing', icon: <CreditCard size={16} /> },
          { label: 'Server Health', path: '/dashboard/admin/health', icon: <Server size={16} /> },
          { label: 'System Operations', path: '/dashboard/admin/operations', icon: <Zap size={16} /> },
          { label: 'Coming Soon', path: '/dashboard/admin/coming-soon-subscribers', icon: <Clock size={16} /> },
          { label: 'View Detections', path: '/dashboard/admin/detections', icon: <Shield size={16} /> },
          { label: 'Audit Logs', path: '/dashboard/admin/audit-logs', icon: <Shield size={16} /> },
          { label: 'Visitor Manifest', path: '/dashboard/admin/visitors', icon: <Activity size={16} /> },
        ].map(link => (
          <button key={link.path} onClick={() => navigate(link.path)} style={{
            padding: 'var(--space-lg)', borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ color: 'var(--accent-primary)' }}>{link.icon}</span> {link.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function OpsCard({ icon, label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)',
      textAlign: 'center', boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ color, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ color: 'var(--text-tertiary)', fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function KPICard({ icon, label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, icon, children }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon} {title}
      </h3>
      {children}
    </div>
  );
}